import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useCategories, useAIPortfolios, useFollowedPortfolios, usePublicPortfolios } from '../services/api';
import { getListPath, flattenFilters, isShareableUserList, isPublicFlag } from '../utils/listUrl';
import { strategyTabId, strategyCaption } from '../utils/strategyTabs';
import ListShareButton from './ListShareButton';
import { IconComponent } from './FilterDropdown';
import './ListsIndexPage.css';

// One row per list. Rows without a stable URL (the watchlist) are dropped, so
// everything shown here is something the router will open. `shareable: false`
// keeps the row's own link and path but swaps the copy button for a lock note
// (`lockLabel` / `lockTitle`, "Only you" unless a row says otherwise): the list
// doesn't open in anyone else's app. Smart lists, public user lists and public or
// followed portfolios are shareable (see isShareableListId in utils/listUrl.js).
const toRow = ({
  id, name, description, icon, shareable = true, depth = 0,
  lockLabel = 'Only you', lockTitle = 'Only you can open this link',
}) => {
  const path = getListPath(id);
  return path
    ? {
      id, path, name: String(name || id), description: String(description || ''), icon, shareable, depth,
      lockLabel, lockTitle,
    }
    : null;
};

const matchesQuery = (row, query) =>
  [row.name, row.id, row.path, row.description].some((value) => value.toLowerCase().includes(query));

const ListRow = ({ row }) => (
  <li
    className={row.depth > 0 ? 'lists-row lists-row-nested' : 'lists-row'}
    style={row.depth > 0 ? { '--lists-depth': row.depth } : undefined}
  >
    <Link to={row.path} className="lists-row-link">
      {row.icon && (
        <span className="lists-row-icon" aria-hidden="true">
          <IconComponent iconName={row.icon} />
        </span>
      )}
      <span className="lists-row-text">
        <span className="lists-row-name">{row.name}</span>
        {row.description && (
          <span className="lists-row-desc" title={row.description}>{row.description}</span>
        )}
      </span>
      <code className="lists-row-path">{row.path}</code>
    </Link>
    {row.shareable ? (
      <ListShareButton path={row.path} compact />
    ) : (
      <span className="lists-row-lock" title={row.lockTitle}>{row.lockLabel}</span>
    )}
  </li>
);

/**
 * /lists — every list and its URL, so /list/<slug> is discoverable rather than
 * something you have to already know. Smart lists are grouped by category (all
 * categories, whatever the picker's timeframe/analysis/sector switch says); the user's
 * own lists, AI portfolios and strategies follow, then the portfolios they follow
 * and other people's public ones. Rows are react-router Links, so opening one is a
 * client-side navigation that ListRouteSync turns into a tab change.
 */
const ListsIndexPage = ({ loggedInUser, userListsData, strategies }) => {
  const [query, setQuery] = useState('');
  const { data: categories, isLoading, error } = useCategories();
  const { data: aiPortfolios } = useAIPortfolios(loggedInUser?.email);
  const { data: followedPortfolios } = useFollowedPortfolios(loggedInUser?.email);
  const { data: publicPortfolios } = usePublicPortfolios(loggedInUser?.email);

  const smartGroups = useMemo(
    () => (Array.isArray(categories) ? categories : [])
      .map((category) => ({
        id: category.id,
        name: category.name || category.id,
        // Nested lists (a filter's `children`) follow their parent, indented.
        rows: flattenFilters(category.filters)
          .map(({ filter, depth }) => toRow({
            id: filter.id,
            name: filter.name,
            description: filter.description || filter.tooltip,
            icon: filter.icon,
            depth,
          }))
          .filter(Boolean),
      }))
      .filter((group) => group.rows.length > 0),
    [categories]
  );

  const userRows = useMemo(() => {
    if (!loggedInUser?.email) return [];
    const lists = (Array.isArray(userListsData) ? userListsData : []).map((list) => {
      // A public list is in every signed-in user's userListsData, so its link works for them (a
      // custom filter excepted: it only loads for its owner). A private one also opens for whoever
      // it was shared with, so it isn't "Only you".
      const isCustomFilter = list.type === 'custom_filter';
      const lock = isPublicFlag(list.is_public) && isCustomFilter
        ? { lockLabel: 'Only you', lockTitle: 'Custom filters only load for their owner' }
        : { lockLabel: 'Private', lockTitle: 'Only you and people this list is shared with can open this link' };
      return toRow({
        id: list.id,
        name: list.name,
        description: list.short_description || list.description || list.tooltip,
        // Same icon the picker gives AI lists
        icon: list.type === 'ai_filter' ? 'magic' : list.icon,
        shareable: isShareableUserList(list),
        ...lock,
      });
    });
    // A private portfolio is left off entirely (its owner still has it in the picker), so its
    // /portfolio/<id> URL is never printed here. Public ones can be shared.
    const portfolios = (Array.isArray(aiPortfolios) ? aiPortfolios : [])
      .filter((portfolio) => !!portfolio.is_public)
      .map((portfolio) => toRow({
        id: portfolio.id,
        name: portfolio.name,
        description: portfolio.description,
        icon: 'SmartToy',
      }));
    // Strategy tabs (issue #337), as the picker lists them under My Lists: the strategy's
    // name, its watch / open-position caption, the Insights icon.
    const strategyRows = (Array.isArray(strategies) ? strategies : []).map((strategy) => toRow({
      id: strategyTabId(strategy.id),
      name: strategy.name,
      description: strategyCaption(strategy),
      icon: 'Insights',
      shareable: false,
    }));
    return [...lists, ...portfolios, ...strategyRows].filter(Boolean);
  }, [loggedInUser, userListsData, aiPortfolios, strategies]);

  // Every portfolio the user owns, private ones included: none of them may resurface under
  // Followed or Public portfolios (a stale follow of one just made private, say).
  const ownedPortfolioIds = useMemo(
    () => new Set([
      ...userRows.map((row) => row.id),
      ...(Array.isArray(aiPortfolios) ? aiPortfolios : []).map((portfolio) => portfolio.id),
    ]),
    [userRows, aiPortfolios]
  );

  // Portfolios the user follows (public by definition: the endpoint only returns those).
  // One that is also the user's own shows up once, under Your lists (or, if it has since
  // gone private, not at all).
  const followedRows = useMemo(() => {
    if (!loggedInUser?.email) return [];
    return (Array.isArray(followedPortfolios) ? followedPortfolios : [])
      .filter((portfolio) => !ownedPortfolioIds.has(portfolio.id))
      .map((portfolio) => toRow({
        id: portfolio.id,
        name: portfolio.name,
        description: portfolio.description,
        icon: 'Visibility', // the picker's "following" icon
      }))
      .filter(Boolean);
  }, [loggedInUser, followedPortfolios, ownedPortfolioIds]);

  // Other people's public portfolios, as the picker's "Public Portfolios" discovery list:
  // minus everything the user owns or already follows.
  const publicRows = useMemo(() => {
    const followedIds = new Set((Array.isArray(followedPortfolios) ? followedPortfolios : []).map((p) => p.id));
    return (Array.isArray(publicPortfolios) ? publicPortfolios : [])
      .filter((portfolio) => !ownedPortfolioIds.has(portfolio.id) && !followedIds.has(portfolio.id))
      .map((portfolio) => toRow({
        id: portfolio.id,
        name: portfolio.name,
        description: portfolio.description,
        icon: 'Public', // the picker's icon for them
      }))
      .filter(Boolean);
  }, [publicPortfolios, followedPortfolios, ownedPortfolioIds]);

  const needle = query.trim().toLowerCase();
  const visibleGroups = useMemo(
    () => (needle
      ? smartGroups
        .map((group) => ({ ...group, rows: group.rows.filter((row) => matchesQuery(row, needle)) }))
        .filter((group) => group.rows.length > 0)
      : smartGroups),
    [smartGroups, needle]
  );
  const visibleUserRows = useMemo(
    () => (needle ? userRows.filter((row) => matchesQuery(row, needle)) : userRows),
    [userRows, needle]
  );

  const visibleFollowedRows = useMemo(
    () => (needle ? followedRows.filter((row) => matchesQuery(row, needle)) : followedRows),
    [followedRows, needle]
  );

  const visiblePublicRows = useMemo(
    () => (needle ? publicRows.filter((row) => matchesQuery(row, needle)) : publicRows),
    [publicRows, needle]
  );

  const hasAnyList = smartGroups.length > 0 || userRows.length > 0 || followedRows.length > 0 || publicRows.length > 0;
  const nothingVisible = visibleGroups.length === 0 && visibleUserRows.length === 0
    && visibleFollowedRows.length === 0 && visiblePublicRows.length === 0;

  return (
    <>
      <Helmet>
        <title>All lists | QuantApe</title>
        <meta name="description" content="Every QuantApe stock list and its URL. Each list lives at /list/<slug>, so you can bookmark it or share it." />
        <link rel="canonical" href="https://quantape.com/lists" />
      </Helmet>

      <main className="lists-index">
        <h1 className="lists-index-title">All lists</h1>
        <p className="lists-index-lede">
          Every list has its own URL, <code>/list/&lt;slug&gt;</code>, that you can bookmark or share.
        </p>

        <input
          type="search"
          className="lists-index-filter"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by name, slug or description"
          aria-label="Filter lists by name, slug or description"
        />

        {isLoading && (
          <div role="status" aria-label="Loading lists">
            {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="lists-skeleton" />)}
          </div>
        )}

        {!isLoading && error && !hasAnyList && (
          <p className="lists-index-status" role="alert">Couldn&apos;t load the lists. Try again in a moment.</p>
        )}

        {!isLoading && !error && !hasAnyList && (
          <p className="lists-index-status">No lists available yet.</p>
        )}

        {hasAnyList && nothingVisible && (
          <p className="lists-index-status">
            No lists match &ldquo;{query.trim()}&rdquo;.
            <button type="button" onClick={() => setQuery('')}>Clear filter</button>
          </p>
        )}

        {visibleGroups.length > 0 && (
          <section aria-labelledby="lists-smart-title">
            <h2 id="lists-smart-title" className="lists-section-title">Smart lists</h2>
            {visibleGroups.map((group) => (
              <div key={group.id}>
                <h3 className="lists-group-title">
                  {group.name} <span className="lists-count">{group.rows.length}</span>
                </h3>
                <ul className="lists-rows">
                  {group.rows.map((row) => <ListRow key={row.id} row={row} />)}
                </ul>
              </div>
            ))}
          </section>
        )}

        {visibleUserRows.length > 0 && (
          <section aria-labelledby="lists-yours-title">
            <h2 id="lists-yours-title" className="lists-section-title">Your lists</h2>
            <ul className="lists-rows">
              {visibleUserRows.map((row) => <ListRow key={row.id} row={row} />)}
            </ul>
          </section>
        )}

        {visibleFollowedRows.length > 0 && (
          <section aria-labelledby="lists-followed-title">
            <h2 id="lists-followed-title" className="lists-section-title">Followed portfolios</h2>
            <ul className="lists-rows">
              {visibleFollowedRows.map((row) => <ListRow key={row.id} row={row} />)}
            </ul>
          </section>
        )}

        {visiblePublicRows.length > 0 && (
          <section aria-labelledby="lists-public-title">
            <h2 id="lists-public-title" className="lists-section-title">Public portfolios</h2>
            <ul className="lists-rows">
              {visiblePublicRows.map((row) => <ListRow key={row.id} row={row} />)}
            </ul>
          </section>
        )}
      </main>
    </>
  );
};

export default ListsIndexPage;
